import React from 'react';
import { InstalledBaseNode, InstalledBaseNodeType, InstalledBaseNodeTypeKey } from "../../codegen/graphql";
import { Box } from "@mui/system";
import { Button } from "@mui/material";
import { Chip, Typography } from "@mui/joy";
import { AccountTreeRounded, AddRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { getInstalledBaseTypeIcon, LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import InstalledBaseEmptyIllustration from "../InstalledBaseTree/InstalledBaseEmptyIllustration";
import { useAllowedChildNodeTypes } from "../../hooks/useAllowedChildNodeTypes";
import { GuidanceAction, getGuidanceActionLabel, resolveEmptyColumnGuidance } from "../../utils/emptyColumnGuidance";
import { getInstalledBaseNodeTypeName } from "../../utils/getInstalledBaseNodeTypeName";

interface Props {
    parentNode: InstalledBaseNode
    searchQuery: string
    filter?: { [key: string]: any }
    canCreate?: boolean
    /** Clears the column search; enables the recovery action of the no-results state. */
    onClearSearch?: () => void
    /** Clears the column filter; enables the recovery action of the no-results state. */
    onClearFilter?: () => void
    /**
     * Requested creation. `undefined` means "let the user pick the type" (the plain
     * fallback button); an action carries the type to jump straight to.
     */
    onCreate?: (action?: GuidanceAction) => void
}

/** Centred column body used by every state. */
const EmptyViewLayout = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '12px',
        pt: "48px",
        pb: "24px",
        px: "4px",
    }}>
        {children}
    </Box>
);

// Roomier than Joy's md default (12px inline / 32px tall), so the type chips read as
// proper tappable tags rather than dense metadata labels.
const CHIP_SX = {
    '--Chip-paddingInline': '14px',
    '--Chip-minHeight': '30px',
};

// Same tonal pair the primary button uses: the theme hardcodes these for
// `variant="contained" color="secondary"`, so there is no palette token to read.
const TONAL_BACKGROUND = '#FDDDCD';
const TONAL_FOREGROUND = '#333333';

/** Tinted circle holding either a node type icon or the generic hierarchy glyph. */
const GuidanceIcon = ({ nodeType }: { nodeType?: InstalledBaseNodeType }) => (
    <Box sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: TONAL_BACKGROUND,
    }}>
        {nodeType
            ? getInstalledBaseTypeIcon(nodeType.key, 36, TONAL_FOREGROUND)
            : <AccountTreeRounded sx={{ fontSize: 36, color: TONAL_FOREGROUND }} />}
    </Box>
);

const InstalledBaseEmptyView = ({
                                    parentNode,
                                    searchQuery,
                                    filter,
                                    canCreate,
                                    onClearSearch,
                                    onClearFilter,
                                    onCreate,
                                }: Props) => {
    const { t } = useTranslation();

    const { loading, error, creatable } = useAllowedChildNodeTypes(parentNode);

    const hasFilter = !!filter && Object.keys(filter).length > 0;
    const isFiltered = !!searchQuery || hasFilter;
    const parentType = getInstalledBaseNodeTypeName(parentNode?.nodeType, t);
    // Creating a child modifies the parent's subtree, so it follows the same `write`
    // permission that gates edit/move (see nodeActions.tsx).
    const canWrite = !!parentNode?.access?.write;

    // 1. Search or filter active: the column may well have children that are simply
    // filtered out, so suggesting what to create here would be misleading.
    if (isFiltered) {
        return (
            <EmptyViewLayout>
                <InstalledBaseEmptyIllustration variant="search" width={200} />
                <Typography level="body" sx={{ color: "text.secondary" }}>
                    {searchQuery
                        ? t('No results for "{{query}}"', { query: searchQuery })
                        : t('No nodes match the active filters.')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: 260 }}>
                    {!!searchQuery && onClearSearch && (
                        <Button variant="outlined" onClick={onClearSearch}>
                            {t('Clear search')}
                        </Button>
                    )}
                    {hasFilter && onClearFilter && (
                        <Button variant="outlined" onClick={onClearFilter}>
                            {t('Clear filters')}
                        </Button>
                    )}
                </Box>
            </EmptyViewLayout>
        );
    }

    // 2. Read-only user: no CTAs to offer.
    if (!canWrite) {
        return (
            <EmptyViewLayout>
                <InstalledBaseEmptyIllustration variant="search" width={200} />
                <Typography level="body" sx={{ color: "text.secondary" }}>
                    {t("No data")}
                </Typography>
            </EmptyViewLayout>
        );
    }

    // 2b. Creation suppressed by the caller (IBM-586: columns inside the trash) —
    // the node's own rules would still allow children, so the guidance CTAs must
    // not render. `undefined` keeps the guidance (callers that don't gate).
    if (canCreate === false) {
        return (
            <EmptyViewLayout>
                <InstalledBaseEmptyIllustration variant="search" width={200} />
                <Typography level="body" sx={{ color: "text.secondary" }}>
                    {t("No data")}
                </Typography>
            </EmptyViewLayout>
        );
    }

    // The type catalogue drives everything below — wait for it rather than rendering a
    // placeholder text that then gets swapped out.
    if (loading) {
        return <LoadingScreen size="sm" />;
    }

    // Catalogue unavailable: fall back to the plain state plus the generic create button,
    // so a failed query can never strand the user without an action.
    if (error) {
        return (
            <EmptyViewLayout>
                <InstalledBaseEmptyIllustration variant="search" width={200} />
                <Typography level="body" sx={{ color: "text.secondary" }}>
                    {t("No data")}
                </Typography>
                {canCreate && onCreate && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => onCreate()}
                        startIcon={<AddRounded fontSize={'small'} />}
                    >
                        {t("Create Node")}
                    </Button>
                )}
            </EmptyViewLayout>
        );
    }

    const guidance = resolveEmptyColumnGuidance(creatable);

    // 3. Nothing can be created here (leaf node, or every allowed type has max: 0).
    if (guidance.variant === 'none') {
        return (
            <EmptyViewLayout>
                <InstalledBaseEmptyIllustration variant="search" width={200} />
                <Typography level="body" sx={{ color: "text.secondary" }}>
                    {t('A {{parentType}} cannot contain any child nodes.', { parentType })}
                </Typography>
            </EmptyViewLayout>
        );
    }

    // Button label colours come from the theme's button variants (contained/secondary is
    // #333333, outlined is #666666); the asset icon has to be told explicitly.
    const actionIcon = (action: GuidanceAction, isPrimary: boolean) =>
        action.kind === 'asset'
            ? getInstalledBaseTypeIcon(InstalledBaseNodeTypeKey.ASSET, 18, isPrimary ? '#333333' : '#666666')
            : <AddRounded fontSize={'small'} />;

    // 4. Guidance. The primary action mirrors the styling of the previous (hover-only)
    // create button; secondaries are outlined. Everything is always visible now.
    const isSingle = guidance.variant === 'single';
    const headlineType = isSingle ? guidance.headlineType : undefined;
    const isAssetHeadline = headlineType?.key === InstalledBaseNodeTypeKey.ASSET;
    const typeName = headlineType ? getInstalledBaseNodeTypeName(headlineType, t) : '';
    const offersAsset =
        guidance.primary.kind === 'asset' ||
        guidance.secondaries.some((action) => action.kind === 'asset');
    const offersOtherTypes = guidance.secondaries.some((action) => action.kind === 'select');

    const eyebrow = isSingle
        ? t('Suggested next step')
        : guidance.hasRecommendations
            ? t('Suggested next steps')
            : t('Available types');

    const headline = isSingle
        ? (isAssetHeadline ? t('Add an asset') : t('Add a {{typeName}}', { typeName }))
        : t('Choose what to add next');

    let body: string;
    if (!isSingle) {
        body = offersAsset
            ? t('A {{parentType}} can be structured in several ways. Pick the type that matches what you want to map at this level, or add an asset directly.', { parentType })
            : t('A {{parentType}} can be structured in several ways. Pick the type that matches what you want to map at this level.', { parentType });
    } else if (isAssetHeadline) {
        body = t('A {{parentType}} can only contain assets. Add one to continue building your structure.', { parentType });
    } else if (guidance.onlyOption) {
        body = t('A {{parentType}} can only contain {{typeName}} nodes. Add one to continue building your structure.', { parentType, typeName });
    } else if (offersOtherTypes && offersAsset) {
        body = t('A {{parentType}} usually contains {{typeName}} nodes. Add one here, create a different node type, or add an asset directly.', { parentType, typeName });
    } else if (offersOtherTypes) {
        body = t('A {{parentType}} usually contains {{typeName}} nodes. Add one here, or create a different node type.', { parentType, typeName });
    } else {
        body = t('A {{parentType}} usually contains {{typeName}} nodes. Add one here, or add an asset directly.', { parentType, typeName });
    }

    return (
        <EmptyViewLayout>
            <GuidanceIcon nodeType={headlineType} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography
                    level="bodySmallBold"
                    sx={{
                        color: 'primary.main',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                    }}
                >
                    {eyebrow}
                </Typography>
                {/* titleSmall is bold and one step below the column header's titleMedium. */}
                <Typography level="titleSmall">{headline}</Typography>
            </Box>

            <Typography level="body" sx={{ color: 'text.secondary' }}>
                {body}
            </Typography>

            {guidance.variant === 'generic' && guidance.badgeTypes.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                    {guidance.badgeTypes.map((nodeType) => (
                        <Chip
                            key={nodeType.id}
                            variant="soft"
                            color="neutral"
                            size="md"
                            sx={CHIP_SX}
                            onClick={() => onCreate?.({ kind: 'type', type: nodeType })}
                        >
                            {getInstalledBaseNodeTypeName(nodeType, t)}
                        </Chip>
                    ))}
                    {guidance.hiddenBadgeCount > 0 && (
                        <Chip variant="plain" color="neutral" size="md" sx={CHIP_SX}>
                            {t('+{{count}} more', { count: guidance.hiddenBadgeCount })}
                        </Chip>
                    )}
                </Box>
            )}

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
                maxWidth: 260,
                mt: '4px',
            }}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => onCreate?.(guidance.primary)}
                    startIcon={actionIcon(guidance.primary, true)}
                >
                    {getGuidanceActionLabel(guidance.primary, t, parentNode?.nodeType?.key)}
                </Button>
                {guidance.secondaries.map((action) => (
                    <Button
                        key={action.kind}
                        variant="outlined"
                        onClick={() => onCreate?.(action)}
                        startIcon={actionIcon(action, false)}
                    >
                        {action.kind === 'select'
                            ? t('Create other node')
                            : getGuidanceActionLabel(action, t, parentNode?.nodeType?.key)}
                    </Button>
                ))}
            </Box>
        </EmptyViewLayout>
    );
}

export default InstalledBaseEmptyView;
